/**
 * Registered devices.
 *
 * **This is a licensing screen wearing a settings costume.** CME's billable unit
 * is the device — "the ability to access" is the event that counts, not whether
 * anyone looked. A user who cannot see and revoke their own devices cannot
 * manage what they are being charged for, and a distributor who cannot produce
 * the list cannot answer an audit. Hence a screen of its own rather than a row
 * in settings.
 *
 * The cap shown is the *effective* one: the lower of the plan's number and the
 * operator's `MAX_DEVICES_PER_USER` ceiling. It is computed server-side, because
 * a client that computed it would eventually disagree with the gate that
 * enforces it.
 */
import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import { api, ApiError, fmtSince, type DevicesResponse } from '../lib/api.js';
import { deviceId } from '../lib/credentials.js';

export function DevicesScreen(): JSX.Element {
  const [data, setData] = useState<DevicesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const thisDevice = deviceId();

  const load = useCallback(async () => {
    try {
      setData(await api.devices());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load devices.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function act(id: string, action: 'revoke' | 'reactivate'): Promise<void> {
    setError(null);
    setBusy(id);
    try {
      if (action === 'revoke') await api.revokeDevice(id);
      else await api.reactivateDevice(id);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not work.');
    } finally {
      setBusy(null);
    }
  }

  if (!data) {
    return (
      <div className="scroll-area">
        <h2>Devices</h2>
        <p className="dim">{error ?? 'Loading…'}</p>
      </div>
    );
  }

  const overCap = data.activeCount > data.cap;
  /**
   * Least recently seen first while over the cap.
   *
   * There is no "first `cap` devices" to keep — every one is denied — so the
   * only useful ordering is the one that puts the likeliest candidates for
   * revocation at the top. Normal order otherwise, because reordering a list
   * somebody is reading is its own small hostility.
   */
  const active = data.devices
    .filter((d) => !d.revokedAt)
    .sort((a, b) => (overCap
      ? Date.parse(a.lastSeenAt) - Date.parse(b.lastSeenAt)
      : 0));
  const revoked = data.devices.filter((d) => d.revokedAt);

  return (
    <div className="scroll-area">
      <div className="section-head">
        <h2>Devices</h2>
        <span className={`chip ${overCap ? 'chip-breached' : 'chip-ok'}`}>
          {data.activeCount} of {data.cap} in use
        </span>
      </div>

      <p className="panel-body">
        Every device that can display market data is registered here, and every one
        of them counts toward your exchange entitlement. Revoking a device signs it
        out immediately and frees a slot.
      </p>

      <p className="panel-body dim">
        Exchanges count devices, not people. A subscriber permitted three or more
        concurrent market-data logins is reported as <em>professional</em>, which
        costs roughly $140 per exchange per month instead of roughly $9 — billed to
        you by your broker. AEGIS holds a single upstream session per user and
        relays it, which is what keeps this list from becoming that.
      </p>

      {error && <div className="form-error" role="alert">{error}</div>}

      {/*
        The previous wording said market data was showing on the first `cap`
        devices and the rest had the journal only. That was not true and it was
        the reason an over-cap account looked like a broken product rather than
        an exceeded limit: the entitlement gate refuses when `active > cap`, so
        the chart is dead on EVERY device until the count comes back down.

        It also now says how many to revoke, because "revoke one" is wrong at
        four of two and the arithmetic is not the reader's job.
      */}
      {overCap && (
        <div className="notice-bar" role="alert">
          <strong>Market data is off on every device.</strong>{' '}
          Your plan permits {data.cap} and {data.activeCount} are registered.
          Exchanges bill per device, so AEGIS withholds live data entirely rather
          than choosing which of your devices counts. Revoke{' '}
          {data.activeCount - data.cap} to bring the chart back.
        </div>
      )}

      <h3 className="panel-h">ACTIVE · {active.length}</h3>
      {active.length === 0 && <p className="panel-empty">No active devices.</p>}
      <ul className="device-list">
        {active.map((d) => (
          <li key={d.id} className="device-row">
            <div className="device-main">
              <span className="device-label">
                {d.label || 'Unnamed device'}
                {d.id === thisDevice && <span className="chip chip-ok device-here">This device</span>}
              </span>
              <span className="device-meta mono">
                {d.platform} · last seen {fmtSince(d.lastSeenAt)}
                {d.marketDataCapable ? '' : ' · journal only'}
              </span>
            </div>
            <button
              className="btn btn-danger compact"
              onClick={() => void act(d.id, 'revoke')}
              disabled={busy === d.id}
            >
              {busy === d.id ? 'Revoking…' : 'Revoke'}
            </button>
          </li>
        ))}
      </ul>

      {revoked.length > 0 && (
        <>
          <h3 className="panel-h">REVOKED · {revoked.length}</h3>
          <ul className="device-list">
            {revoked.map((d) => (
              <li key={d.id} className="device-row device-revoked">
                <div className="device-main">
                  <span className="device-label">{d.label || 'Unnamed device'}</span>
                  <span className="device-meta mono">
                    {d.platform} · revoked {fmtSince(d.revokedAt!)}
                  </span>
                </div>
                <button
                  className="btn btn-quiet compact"
                  onClick={() => void act(d.id, 'reactivate')}
                  disabled={busy === d.id}
                >
                  {busy === d.id ? 'Restoring…' : 'Reactivate'}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
